import { Account } from '@packages/models/api';
import { Constants } from '@packages/core/config';
import { configService } from '@web/services/singletons';

export const canAccessSTIS = (account: Account | null): boolean => {
    return account?.docsCapabilities?.canViewSTISPublications ?? false;
};

export const hasStisThirdPartyRole = (account: Account | null | undefined): boolean => {
    return account?.baseRoles?.some((role) => role.roleId === 'STIS_THIRD_PARTY') ?? false;
};

const FALLBACK_SUBARU_DIAGNOSTICS_URL = 'https://www.subarudiagnostics.com';

export const getSubaruDiagnosticsUrl = (): string => {
    return (
        configService.get(Constants.Env.SubaruDiagnosticsUrl, FALLBACK_SUBARU_DIAGNOSTICS_URL) ??
        FALLBACK_SUBARU_DIAGNOSTICS_URL
    );
};

export type SubDurationBucket = 'THREE_DAY' | 'MONTH' | 'YEAR';

const inferDurationBucket = (startDate: Date, expiryDate: Date): SubDurationBucket => {
    const durationDays = Math.round((expiryDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (durationDays <= 4) return 'THREE_DAY';
    if (durationDays <= 45) return 'MONTH';
    return 'YEAR';
};

const MILESTONE_RANGES: Record<SubDurationBucket, Array<{ min: number; max: number }>> = {
    THREE_DAY: [{ min: 0, max: 1 }],
    MONTH: [
        { min: 4, max: 7 },
        { min: 2, max: 3 },
        { min: 0, max: 1 },
    ],
    YEAR: [
        { min: 8, max: 30 },
        { min: 2, max: 7 },
        { min: 0, max: 1 },
    ],
};

const isInMilestoneWindow = (bucket: SubDurationBucket, daysUntilExpiry: number): boolean => {
    return MILESTONE_RANGES[bucket].some(({ min, max }) => daysUntilExpiry >= min && daysUntilExpiry <= max);
};

export interface StisSubscriptionExpiry {
    expiresAt: Date;
    durationBucket: SubDurationBucket;
    daysUntilExpiry: number;
    shouldShowBanner: boolean;
}

export const getStisSubscriptionExpiry = (account: Account | null | undefined): StisSubscriptionExpiry | null => {
    if (!hasStisThirdPartyRole(account)) return null;

    const sub = account?.subscription;
    if (!sub?.expiryDate || !sub?.startDate) return null;
    if (sub.status === 'pending') return null;

    const expiresAt = new Date(sub.expiryDate);
    const startDate = new Date(sub.startDate);
    if (Number.isNaN(expiresAt.getTime()) || Number.isNaN(startDate.getTime())) return null;
    const durationBucket = inferDurationBucket(startDate, expiresAt);

    if (sub.status === 'expired') {
        return { expiresAt, durationBucket, daysUntilExpiry: 0, shouldShowBanner: false };
    }

    const now = new Date();
    // Calendar-day diff: strip time component so "expires later today" = 0 days,
    // "expires tomorrow" = 1 day, making 0-day milestone windows reachable.
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfExpiryDay = new Date(expiresAt.getFullYear(), expiresAt.getMonth(), expiresAt.getDate());
    const msRemaining = startOfExpiryDay.getTime() - startOfToday.getTime();

    // If expiry date is in the past, treat as expired regardless of status
    if (msRemaining < 0) {
        return { expiresAt, durationBucket, daysUntilExpiry: 0, shouldShowBanner: false };
    }

    const daysUntilExpiry = Math.round(msRemaining / (1000 * 60 * 60 * 24));

    // Same calendar day — check if the actual expiry timestamp has passed
    if (daysUntilExpiry === 0 && now >= expiresAt) {
        return { expiresAt, durationBucket, daysUntilExpiry: 0, shouldShowBanner: false };
    }

    const shouldShowBanner = isInMilestoneWindow(durationBucket, daysUntilExpiry);

    return { expiresAt, durationBucket, daysUntilExpiry, shouldShowBanner };
};

export type StisAccessState = 'INTERNAL_USER' | 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED' | 'PENDING' | 'NO_SUBSCRIPTION';

export const getStisAccessState = (account: Account | null | undefined): StisAccessState => {
    if (!hasStisThirdPartyRole(account)) return 'INTERNAL_USER';

    const expiry = getStisSubscriptionExpiry(account);

    if (!expiry) {
        const sub = account?.subscription;
        if (!sub) return 'NO_SUBSCRIPTION';
        if (sub.status === 'expired') return 'EXPIRED';
        if (sub.status === 'pending') return 'PENDING';
        return 'NO_SUBSCRIPTION';
    }

    if (expiry.daysUntilExpiry === 0) {
        // Expiry day is today — check if the actual timestamp has passed
        const now = new Date();
        if (now >= expiry.expiresAt) return 'EXPIRED';
        return 'EXPIRING_SOON';
    }
    if (expiry.shouldShowBanner) return 'EXPIRING_SOON';
    return 'ACTIVE';
};
